import { useGetIdentity } from "@refinedev/core";
import { Navigate, useParams, useSearchParams } from "react-router";

import type { UserInfo } from "../types/auth";
import { ADMIN_USER_TYPES } from "../types/auth";
import { LoadingSpinner } from "./common/LoadingSpinner";

/**
 * Neutral landing route for planning-comment email links.
 *
 * Notification emails go out to a mixed audience: admins/coordinators
 * (who navigate via `/planning-groups/show/:id`) and auditors (who
 * live under `/auditor/planning-groups/:id`). Hard-coding either path
 * in the email breaks the link for the other role. This shim reads
 * the current identity and forwards to whichever detail route the
 * user's guards will accept, preserving the query string (typically
 * `?tab=comments&ticket=<id>` so the comments drawer opens on the
 * exact thread).
 */
export const PlanningCommentRedirect = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { data: identity, isLoading } = useGetIdentity<UserInfo>();

  if (isLoading) {
    return <LoadingSpinner fullScreen tip="Redirigiendo…" />;
  }

  const userType = identity?.app_user?.user_type;
  const isAdmin =
    userType !== undefined &&
    (ADMIN_USER_TYPES as readonly number[]).includes(userType);

  // Auditores y managers (user_type 3-5) van a la ruta auditor;
  // admins (1-2) a la admin. En ambos routes se monta el mismo
  // PlanningGroupShow y respeta `?tab=comments&ticket=X`.
  const base = isAdmin
    ? `/planning-groups/show/${id}`
    : `/auditor/planning-groups/${id}`;
  const search = searchParams.toString();
  const target = search ? `${base}?${search}` : base;
  return <Navigate to={target} replace />;
};
